'use client';

import type { ReactNode } from 'react';
import type { Store } from '../../../lib/types';
import { cn } from '../../../lib/utils';
import { AnimatedAccordionIcon } from '../../animated-accordion-icon';
import { CustomLink } from '../../custom-link';
import { iconsTileCloud } from '../../icons-tilecloud';
import { iconsTrade } from '../../icons-trade';
import { iconsYabby } from '../../icons-yabby';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../ui/accordion';
import type { ProductPageProps } from './client';

const DESCRIPTION_NAME = 'Product details';

type ValidSites = Extract<Store, 'tilecloud' | 'yabby' | 'trade' | 'dawn'>;

type Attribute = ProductPageProps['attributes'][number];

function isValidIconKey(key: string, site: ValidSites): boolean {
  const icons: Record<ValidSites, Record<string, ReactNode>> = {
    tilecloud: iconsTileCloud,
    yabby: iconsYabby,
    trade: iconsTrade,
    dawn: iconsTileCloud,
  };
  return key in icons[site];
}

interface ProductAccordionRootProps {
  children: ReactNode;
  className?: string;
  defaultValue?: string[];
}

function ProductAccordionRoot({
  children,
  className = 'col-span-12',
  defaultValue = [DESCRIPTION_NAME],
}: ProductAccordionRootProps) {
  return (
    <div className={className}>
      <Accordion
        openMultiple={true}
        className="grid gap-[--product-grid-gap] text-[hsl(var(--color))]"
        defaultValue={defaultValue}
      >
        {children}
      </Accordion>
    </div>
  );
}

interface ProductAccordionItemProps {
  title: string;
  children: ReactNode;
  icon?: ReactNode;
  value: string;
}

function ProductAccordionItem({
  title,
  children,
  icon,
  value,
}: ProductAccordionItemProps) {
  return (
    <AccordionItem value={value}>
      <div className="grid border-b border-b-[hsl(var(--color))]/50">
        <AccordionTrigger className="group block w-full appearance-none">
          <div className="cursor-pointer select-none py-2.5">
            <span className="flex items-center justify-between">
              <h2 className="font-semibold text-sm">
                <div className="flex items-center gap-2 [&>svg]:size-4">
                  {icon}
                  <span className="text-[hsl(var(--color))]">{title}</span>
                </div>
              </h2>
              <AnimatedAccordionIcon />
            </span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="overflow-hidden">
          {children}
        </AccordionContent>
      </div>
    </AccordionItem>
  );
}

interface AttributesTableProps {
  attributes: Array<Attribute | null>;
}

function AttributesTable({ attributes }: AttributesTableProps) {
  return (
    <table className="mt-4 mb-8 w-full">
      <tbody className="grid gap-1">
        {attributes.map((attribute, index) => {
          if (!attribute) return null;
          return <AttributeItem key={index} attribute={attribute} />;
        })}
      </tbody>
    </table>
  );
}

function AttributeRow({
  title,
  children,
}: { title: string; children: ReactNode }) {
  return (
    <tr className="grid grid-cols-2 gap-4 py-2 text-sm">
      <td className="font-medium">{title}</td>
      <td>{children}</td>
    </tr>
  );
}

function AttributeLink({
  href,
  children,
  target,
}: { href: string; children: ReactNode; target?: string }) {
  return (
    <CustomLink
      href={href}
      className="font-semibold text-[--color] underline hover:underline"
      target={target}
    >
      {children}
    </CustomLink>
  );
}

function AttributeItem({
  attribute,
}: {
  attribute: Attribute;
}) {
  if (attribute.type === 'referenceToText') {
    return (
      <AttributeRow title={attribute.title as string}>
        {attribute.value as string}
      </AttributeRow>
    );
  }

  if (
    attribute.type === 'referenceToLink' &&
    attribute.link &&
    'slug' in attribute.link
  ) {
    return (
      <AttributeRow title={attribute.title as string}>
        <AttributeLink href={attribute.link.slug}>
          {attribute.link.title}
        </AttributeLink>
      </AttributeRow>
    );
  }

  if (attribute.type === 'arrayOfReferencesToTexts' && attribute.values) {
    return (
      <AttributeRow title={attribute.title as string}>
        <div className="grid gap-2">
          {attribute.values.map((value, index) => (
            <div key={index}>{value}</div>
          ))}
        </div>
      </AttributeRow>
    );
  }

  if (attribute.type === 'boolean') {
    return (
      <AttributeRow title={attribute.title as string}>
        {attribute.value ? 'Yes' : 'No'}
      </AttributeRow>
    );
  }

  if (attribute.type === 'specificationAndInstallation' && attribute.links) {
    return (
      <AttributeRow title={attribute.title as string}>
        <div className="grid gap-1">
          {attribute.links.map((link, index) => (
            <div key={index}>
              <AttributeLink href={link?.slug ?? ''} target="blank">
                {link.title ?? 'Download here'}
              </AttributeLink>
            </div>
          ))}
        </div>
      </AttributeRow>
    );
  }

  return null;
}

interface ProductAccordionDescriptionProps {
  children: ReactNode;
  attributes?: Array<Attribute | null> | null;
  className?: string;
}

function ProductAccordionDescription({
  children,
  attributes,
  className,
}: ProductAccordionDescriptionProps) {
  return (
    <AccordionItem value={DESCRIPTION_NAME}>
      <div
        className={cn(
          'grid border-b border-b-[hsl(var(--color))]/50',
          className
        )}
      >
        <AccordionTrigger className="group block w-full appearance-none">
          <div className="cursor-pointer select-none py-2.5">
            <span className="flex items-center justify-between">
              <h2 className="font-semibold text-sm">
                <div className="flex items-center gap-2 [&>svg]:size-4">
                  <InfoIcon />
                  <span className="text-[hsl(var(--color))]">
                    {DESCRIPTION_NAME}
                  </span>
                </div>
              </h2>
              <AnimatedAccordionIcon />
            </span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="overflow-hidden">
          {children}
          {attributes && attributes.length > 0 && (
            <AttributesTable attributes={attributes} />
          )}
        </AccordionContent>
      </div>
    </AccordionItem>
  );
}

interface ProductAccordionGenericItemProps {
  site: ValidSites;
  title: string;
  children: ReactNode;
  icon?: string;
  value: string;
  dynamicContent?: string[] | null;
  productDownloads?: Array<{
    _type: 'fileType' | 'imageType';
    title?: string | null;
    file?: {
      asset?: {
        url?: string | null;
      } | null;
    } | null;
    image?: {
      asset?: {
        url?: string | null;
      } | null;
    } | null;
  }> | null;
}

function ProductAccordionGenericItem({
  site,
  title,
  children,
  icon,
  value,
  dynamicContent,
  productDownloads,
}: ProductAccordionGenericItemProps) {
  const icons: Record<ValidSites, Record<string, ReactNode>> = {
    tilecloud: iconsTileCloud,
    yabby: iconsYabby,
    trade: iconsTrade,
    dawn: iconsTileCloud,
  };
  const isValidIcon = icon && isValidIconKey(icon, site);
  const iconElement = isValidIcon ? icons[site][icon] : null;

  return (
    <AccordionItem value={value}>
      <div className="grid border-b border-b-[hsl(var(--color))]/50">
        <AccordionTrigger className="group block w-full appearance-none">
          <div className="cursor-pointer select-none py-2.5">
            <span className="flex items-center justify-between">
              <h2 className="font-semibold text-sm">
                <div className="flex items-center gap-2 [&>svg]:size-4">
                  {iconElement}
                  <span className="text-[hsl(var(--color))]">{title}</span>
                </div>
              </h2>
              <AnimatedAccordionIcon />
            </span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="overflow-hidden">
          {children}
          {dynamicContent?.includes('productDownloads') && productDownloads && (
            <div className="-mt-2">
              <ProductDownloadsTable downloads={productDownloads} />
            </div>
          )}
        </AccordionContent>
      </div>
    </AccordionItem>
  );
}

function InfoIcon() {
  return (
    <svg
      xmlnsXlink="http://www.w3.org/1999/xlink"
      xmlns="http://www.w3.org/2000/svg"
      className="size-5"
      fill="none"
      viewBox="0 0 24 24"
      width="24"
      height="24"
    >
      <title>Info</title>
      <path
        fill="hsl(var(--color))"
        fillRule="evenodd"
        d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm10-4a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H12a1 1 0 0 1-1-1Zm1 3a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0v-4a1 1 0 0 1 1-1Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

interface ProductDownloadsTableProps {
  downloads: Array<{
    _type: 'fileType' | 'imageType';
    title?: string | null;
    file?: {
      asset?: {
        url?: string | null;
      } | null;
    } | null;
    image?: {
      asset?: {
        url?: string | null;
      } | null;
    } | null;
  }>;
}

function ProductDownloadsTable({ downloads }: ProductDownloadsTableProps) {
  return (
    <table className="mt-4 mb-8 w-full">
      <tbody className="grid gap-1">
        <AttributeRow title="Downloads">
          <div className="grid gap-1">
            {downloads.map((download, index) => {
              let url: string | null | undefined;
              let title: string;

              switch (download._type) {
                case 'fileType': {
                  url = download.file?.asset?.url ?? undefined;
                  title = download.title ?? 'Download here';
                  break;
                }
                case 'imageType':
                  // biome-ignore lint/style/useSingleCaseStatement: <explanation>
                  url = download.image?.asset?.url ?? undefined;
                  title = download.title ?? 'Download here';
                  break;
                default:
                  return null;
              }

              if (!url) return null;

              return (
                <div key={index}>
                  <AttributeLink href={url} target="blank">
                    {title}
                  </AttributeLink>
                </div>
              );
            })}
          </div>
        </AttributeRow>
      </tbody>
    </table>
  );
}

ProductAccordionRoot.Item = ProductAccordionItem;
ProductAccordionRoot.Description = ProductAccordionDescription;
ProductAccordionRoot.GenericItem = ProductAccordionGenericItem;
ProductAccordionRoot.AttributesTable = AttributesTable;
ProductAccordionRoot.DownloadsTable = ProductDownloadsTable;

export const ProductDetailsAccordion = ProductAccordionRoot;
